import { AvailableWorker } from '../api/users'
import { WorkersRequest, WorkersRequestPost } from '../api/workers-requests'
import { isAuthorizedFor } from '../components/protected/protected'
import { useAvailableWorkersQuery } from '../queries/users'
import { useMemo } from 'react'
import { useMissionsQuery } from '../queries/missions'
import { compareDocsById, getDocIdAsString } from '../utils/documents'
import { startOfDay, subMonths } from 'date-fns'
import { deduplicateByKey, sortByIgnoreCase } from '../utils/arrays'
import { JobTitle } from '../api/job-titles'
import { sortBy } from 'lodash'

export type AvailableWorkerWithPostSuggestionData = AvailableWorker & {
  desiredJobTitleIds: string[] // IDs of job titles where this worker is marked as desired
  missionCorrespondingJobTitleIds: string[] // IDs of job titles where this worker is marked as corresponding
  lastMissionStartDate: Date | null
  isInactiveWithEmployer: boolean
}

export type WorkersRequestPostsSuggestions = {
  jobTitles: JobTitle[]
  workers: AvailableWorkerWithPostSuggestionData[]
  availablePosts: WorkersRequestPost[] // Posts where this worker is available
}

interface UseWorkersRequestPostsSuggestionsProps {
  workersRequest?: WorkersRequest
  correspondingMissions?: {
    maxMonths?: number
  }
}

/**
 * Finds suggested posts for a workers request
 * @param workersRequest - The workers request
 * @param existingMissions - The existing missions for the workers request
 * @returns The suggested posts for the workers request by order of priority:
 * - Desired workers
 * - Workers with corresponding missions
 * - Available workers
 * - Complete suggested posts
 */
const useWorkersRequestPostsSuggestions = ({
  workersRequest,
  correspondingMissions = { maxMonths: 2 },
}: UseWorkersRequestPostsSuggestionsProps): WorkersRequestPostsSuggestions => {
  // Récupérer les posts remplaçables
  const posts = (workersRequest?.posts || []).filter(post => post.isReplaceable)
  const hasReplaceablePosts = posts.length > 0
  // Récupérer les workers disponibles
  const availableWorkersQuery = useAvailableWorkersQuery(
    {
      workersRequestId: workersRequest?._id,
    },
    {
      enabled:
        Boolean(workersRequest) && isAuthorizedFor(['interimAgencyMember']) && hasReplaceablePosts,
    },
  )
  const availableWorkers = sortByIgnoreCase(availableWorkersQuery.data || [], 'lastName')

  // Récupérer les missions correspondant au poste et à l'employeur
  const jobTitllesIds = new Set(posts.map(post => getDocIdAsString(post.jobTitle)))
  const missionsQuery = useMissionsQuery(
    {
      jobTitle: Array.from(jobTitllesIds),
      employer: workersRequest?.employer?._id ? [workersRequest?.employer?._id] : [],
      start: subMonths(startOfDay(new Date()), correspondingMissions.maxMonths || 300),
      //@ts-expect-error
      summary: null,
      populate: ['user'],
    },
    {
      enabled:
        Boolean(workersRequest) &&
        Boolean(workersRequest?.employer) &&
        isAuthorizedFor(['interimAgencyMember']) &&
        posts.length > 0,
    },
  )
  const existingMissions = missionsQuery.data || []

  const allJobTitles = useMemo(() => {
    return deduplicateByKey(
      posts.map(post => post.jobTitle),
      '_id',
    )
  }, [posts])

  // Memoize the complete result to avoid recalculating when dependencies haven't changed
  const workers = useMemo(() => {
    const workers: AvailableWorkerWithPostSuggestionData[] = []

    availableWorkers.forEach(availableWorker => {
      // Récupérer les job titles des posts où le worker est marqué comme désiré
      const desiredJobTitles = new Set(
        posts
          .filter(post =>
            post.desiredWorkers.some(worker => compareDocsById(worker, availableWorker)),
          )
          .map(post => getDocIdAsString(post.jobTitle)),
      )

      const workerMissions = sortBy(
        existingMissions.filter(mission => compareDocsById(mission.user, availableWorker)),
        'start',
      )

      // Récupérer les job titles des missions correspondant au worker
      const missionCorrespondingJobTitles = new Set(
        workerMissions.map(mission => getDocIdAsString(mission.jobTitle)),
      )

      // Récupérer si le worker est inactif avec l'employeur lié à la demande
      const relationWithEmployer =
        workersRequest?.employer &&
        availableWorker.employerRelations.find(relation =>
          compareDocsById(relation.employer, workersRequest.employer),
        )
      const isInactiveWithEmployer = relationWithEmployer?.collaboration?.isInactive || false

      workers.push({
        ...availableWorker,
        desiredJobTitleIds: Array.from(desiredJobTitles),
        missionCorrespondingJobTitleIds: Array.from(missionCorrespondingJobTitles),
        lastMissionStartDate: workerMissions[0]?.start || null,
        isInactiveWithEmployer,
      })
    })

    return workers
  }, [posts, availableWorkers, existingMissions])

  return {
    jobTitles: allJobTitles,
    workers,
    availablePosts: posts,
  }
}

export default useWorkersRequestPostsSuggestions
